<!--

 -->

<template>
  <div class="gsl-hrm-evaluation gsl-hrm-card-container">
    <oxd-text class="gsl-hrm-main-title">
      {{ title }}
    </oxd-text>

    <div class="gsl-hrm-evaluation-header">
      <oxd-grid :cols="3" class="gsl-hrm-evaluation-header-grid">
        <oxd-grid-item class="gsl-hrm-evaluation-title">
          <img
            class="gsl-hrm-evaluation-title-profile-image"
            alt="profile picture"
            :src="profileImgSrc"
          />
          <div class="gsl-hrm-evaluation-title-name">
            <oxd-text type="card-title">
              {{ employeeName }}
            </oxd-text>
            <oxd-text type="card-body">
              {{ jobTitle }}
            </oxd-text>
          </div>
        </oxd-grid-item>
        <oxd-grid-item>
          <oxd-text type="card-body">
            {{ $t('general.status') }}
          </oxd-text>
          <oxd-text type="card-title">
            {{ evaluationLabel }}
          </oxd-text>
        </oxd-grid-item>
      </oxd-grid>
      <oxd-icon-button
        v-if="collapsible"
        :with-container="false"
        :name="isCollapsed ? 'chevron-down' : 'chevron-up'"
        @click="toggleForm"
      />
    </div>

    <template v-if="!isCollapsed">
      <oxd-divider />
      <oxd-grid :cols="4" class="gsl-hrm-evaluation-grid">
        <oxd-grid-item class="gsl-hrm-evaluation-grid-header">
          <oxd-text type="subtitle-2">{{ $t('general.kpis') }}</oxd-text>
        </oxd-grid-item>
        <oxd-grid-item class="gsl-hrm-evaluation-grid-header">
          <oxd-text type="subtitle-2">{{ $t('performance.rating') }}</oxd-text>
        </oxd-grid-item>
        <oxd-grid-item class="gsl-hrm-evaluation-grid-header">
          <oxd-text type="subtitle-2">{{ $t('general.comments') }}</oxd-text>
        </oxd-grid-item>
        <oxd-grid-item
          class="gsl-hrm-evaluation-grid-spacer-md"
        ></oxd-grid-item>

        <template v-for="(kpiGroup, job) in groupedKpis" :key="job">
          <!-- Job Title Header -->
          <oxd-grid-item class="--span-column-4">
            <oxd-text type="subtitle-1" class="gsl-hrm-evaluation-grid-kpi-main"
              >{{ job }} ({{ kpiGroup.length }})</oxd-text
            >
            <oxd-divider />
          </oxd-grid-item>

          <!-- KPIs under this Job Title -->
          <template v-for="kpi in kpiGroup" :key="kpi.id">
            <oxd-grid-item class="gsl-hrm-evaluation-grid-kpi">
              <oxd-text
                class="gsl-hrm-evaluation-grid-kpi-header"
                type="subtitle-2"
              >
                {{ $t('performance.kpi') }}
              </oxd-text>
              <oxd-text
                v-tooltip="kpi.description"
                tag="p"
                class="gsl-hrm-evaluation-grid-kpi-label"
              >
                {{ kpi.title }}
              </oxd-text>
              <oxd-text class="gsl-hrm-evaluation-grid-kpi-minmax" tag="p">
                {{ $t('performance.min') }} - {{ $t('performance.max') }}: ({{
                  kpi.minRating
                }}
                - {{ kpi.maxRating }})
              </oxd-text>
            </oxd-grid-item>

            <!-- Rating -->
            <oxd-grid-item>
              <oxd-text
                class="gsl-hrm-evaluation-grid-kpi-header"
                type="subtitle-2"
              >
                {{ $t('performance.rating') }}
              </oxd-text>
              <oxd-input-field
                type="input"
                :disabled="!editable"
                :rules="rules[kpi.globalIndex]"
                :model-value="modelValue.kpis[kpi.globalIndex].rating"
                @update:model-value="onUpdateRating($event, kpi.globalIndex)"
              />
            </oxd-grid-item>

            <!-- Comment -->
            <oxd-grid-item>
              <oxd-text
                class="gsl-hrm-evaluation-grid-kpi-header"
                type="subtitle-2"
              >
                {{ $t('general.comment') }}
              </oxd-text>
              <oxd-input-field
                class="gsl-hrm-evaluation-grid-comment"
                rows="2"
                type="textarea"
                :disabled="!editable"
                :rules="commentValidators"
                :model-value="modelValue.kpis[kpi.globalIndex].comment"
                @update:model-value="onUpdateComment($event, kpi.globalIndex)"
              />
            </oxd-grid-item>

            <oxd-grid-item
              class="gsl-hrm-evaluation-grid-spacer-md"
            ></oxd-grid-item>
          </template>
        </template>
      </oxd-grid>

      <oxd-divider />
      <oxd-grid :cols="3" class="gsl-hrm-evaluation-grid">
        <oxd-grid-item class="gsl-hrm-evaluation-grid-general">
          <oxd-text tag="p" class="gsl-hrm-evaluation-grid-general-label">
            {{ $t('performance.general_comment') }}
          </oxd-text>
        </oxd-grid-item>
        <oxd-grid-item class="--span-column-2">
          <oxd-input-field
            class="gsl-hrm-evaluation-grid-comment"
            rows="2"
            type="textarea"
            :disabled="!editable"
            :rules="commentValidators"
            :model-value="modelValue.generalComment"
            @update:model-value="onUpdateGeneralComment($event)"
          />
        </oxd-grid-item>
      </oxd-grid>
      <slot></slot>
    </template>
  </div>
</template>

<script>
import {computed, ref} from 'vue';
import usei18n from '@/core/util/composable/usei18n';
import {shouldNotExceedCharLength} from '@/core/util/validation/rules';
import infoTooltipDirective from '@/core/util/directives/infoTooltip';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';
import {OxdDivider} from '@ohrm/oxd';

const defaultPic = `${window.appGlobal.publicPath}/images/default-photo.png`;

export default {
  components: {
    'oxd-divider': OxdDivider,
  },
  directives: {
    tooltip: infoTooltipDirective,
  },
  props: {
    kpis: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    editable: {
      type: Boolean,
      required: true,
    },
    collapsible: {
      type: Boolean,
      required: true,
    },
    employee: {
      type: Object,
      required: true,
    },
    jobTitle: {
      type: String,
      required: true,
    },
    rules: {
      type: Array,
      required: true,
    },
    modelValue: {
      type: Object,
      required: true,
      validator: (value) =>
        Object.hasOwn(value, 'kpis') && Object.hasOwn(value, 'generalComment'),
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
    status: {
      type: Number,
      required: true,
    },
  },

  emits: ['update:modelValue'],

  setup(props, context) {
    const {$t} = usei18n();
    const {$tEmpName} = useEmployeeNameTranslate();
    const isCollapsed = ref(props.collapsed);
    const commentValidators = [shouldNotExceedCharLength(2000)];

    const profileImgSrc = computed(() => {
      return props.employee.empNumber
        ? `${window.appGlobal.baseUrl}/pim/viewPhoto/empNumber/${props.employee.empNumber}`
        : defaultPic;
    });

    const employeeName = computed(() => {
      return $tEmpName(props.employee);
    });

    const toggleForm = () => {
      isCollapsed.value = !isCollapsed.value;
    };

    const onUpdateRating = (value, index) => {
      context.emit('update:modelValue', {
        kpis: props.modelValue.kpis.map((item, _index) => {
          if (_index === index) {
            return {...item, rating: value};
          }
          return item;
        }),
        generalComment: props.modelValue.generalComment,
      });
    };

    const onUpdateComment = (value, index) => {
      context.emit('update:modelValue', {
        kpis: props.modelValue.kpis.map((item, _index) => {
          if (_index === index) {
            return {...item, comment: value};
          }
          return item;
        }),
        generalComment: props.modelValue.generalComment,
      });
    };

    const onUpdateGeneralComment = (value) => {
      context.emit('update:modelValue', {
        kpis: props.modelValue.kpis,
        generalComment: value,
      });
    };

    const statusOpts = [
      {id: 1, label: $t('performance.evaluation_activated')},
      {id: 2, label: $t('performance.evaluation_in_progress')},
      {id: 3, label: $t('performance.evaluation_completed')},
    ];

    const evaluationLabel = computed(
      () => statusOpts.find((el) => el.id === props.status).label,
    );

    const groupedKpis = computed(() => {
      const groups = {};
      props.kpis.forEach((kpi, index) => {
        const jt = kpi.jobTitle?.name || $t('performance.mandatory_kpis');
        if (!groups[jt]) {
          groups[jt] = [];
        }
        groups[jt].push({
          ...kpi,
          globalIndex: index, // preserve continuous index
        });
      });
      return groups;
    });

    return {
      toggleForm,
      isCollapsed,
      employeeName,
      profileImgSrc,
      onUpdateRating,
      onUpdateComment,
      onUpdateGeneralComment,
      commentValidators,
      evaluationLabel,
      groupedKpis,
    };
  },
};
</script>

<style src="./evaluation-form.scss" lang="scss" scoped></style>
